import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Plus, Loader2, Check, XCircle, ChevronUp, History, CircleDot, CheckCircle2, MinusCircle, ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STEP_ICON = { pending: CircleDot, executing: Loader2, completed: CheckCircle2, failed: XCircle, skipped: MinusCircle };
const STEP_COLOR = { pending: "text-zinc-500", executing: "text-cyan-300", completed: "text-emerald-400", failed: "text-rose-400", skipped: "text-zinc-600" };

const PROMPTS = ["Find me a domain for my new studio", "Open my approvals", "Research 3 competitors and draft outreach to each", "Which agents are on autopilot?"];

export default function CommandDock({ page, onNavigate, onRefresh, onFocusAgent }) {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(null);
  const [session, setSession] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [recents, setRecents] = useState([]);
  const [showRecents, setShowRecents] = useState(false);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const messages = session?.messages || [];

  const scrollDown = useCallback(() => { setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50); }, []);

  const ensureSession = useCallback(async () => {
    if (sid) return sid;
    const s = await api.createInputSession();
    setSid(s.id); setSession({ ...s, messages: [] });
    return s.id;
  }, [sid]);

  const loadRecents = useCallback(async () => { try { setRecents(await api.getInputSessions()); } catch {} }, []);

  useEffect(() => { if (open) loadRecents(); }, [open, loadRecents]);

  // Poll while any plan is running so step progress streams in.
  useEffect(() => {
    const running = messages.some((m) => m.plan?.status === "running");
    clearInterval(pollRef.current);
    if (running && sid) {
      pollRef.current = setInterval(async () => {
        try { const s = await api.getInputSession(sid); setSession(s); scrollDown(); } catch {}
      }, 2000);
    }
    return () => clearInterval(pollRef.current);
  }, [messages, sid, scrollDown]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true); setInput("");
    try {
      const id = await ensureSession();
      // optimistic user bubble
      setSession((prev) => ({ ...prev, messages: [...(prev?.messages || []), { id: `tmp-${Date.now()}`, role: "user", content: msg }] }));
      scrollDown();
      const updated = await api.inputMessage(id, msg, page);
      setSession(updated); scrollDown(); loadRecents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send that. Try again.");
      setSession((prev) => prev ? { ...prev, messages: prev.messages.filter((m) => !String(m.id).startsWith("tmp-")) } : prev);
    } finally { setSending(false); }
  };

  const applyResult = (result) => {
    if (!result) return;
    if (result.focus_agent) onFocusAgent?.(result.focus_agent);
    else if (result.navigate) onNavigate?.(result.navigate);
    if (result.refresh) onRefresh?.();
  };

  const decideAction = async (mid, approved) => {
    try {
      const res = await api.inputDecision(sid, mid, approved);
      setSession(res.session); scrollDown();
      if (approved) applyResult(res.result);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const decidePlan = async (mid, approved) => {
    try {
      const res = await api.inputPlanDecision(sid, mid, approved);
      setSession(res.session); scrollDown();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start the plan"); }
  };

  const buyDomain = async (r) => {
    try {
      const res = await api.domainCheckout(r.domain, r.tld, window.location.origin);
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Connect Porkbun in Domains first"); onNavigate?.("store"); }
  };

  const openSession = async (id) => {
    setShowRecents(false);
    try { const s = await api.getInputSession(id); setSid(id); setSession(s); scrollDown(); } catch {}
  };

  const newConversation = () => { setSid(null); setSession(null); setShowRecents(false); };

  // Collapsed pill
  if (!open) {
    return (
      <button
        data-testid="command-dock-open"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 h-11 pl-3 pr-4 rounded-full bg-[#0b1220]/95 backdrop-blur-xl border border-cyan-400/40 text-cyan-200 shadow-[0_0_28px_rgba(0,240,255,0.25)] hover:border-cyan-300/70 transition-colors"
        title="Ask LifeOS to do anything"
      >
        <span className="w-7 h-7 grid place-items-center rounded-full bg-cyan-500/20"><Sparkles size={15} /></span>
        <span className="text-sm font-tactical font-semibold tracking-wide hidden sm:inline">Ask LifeOS</span>
      </button>
    );
  }

  return (
    <div data-testid="command-dock" className="fixed bottom-4 right-4 z-50 w-[min(420px,calc(100vw-2rem))] rounded-2xl bg-[#080d16]/97 backdrop-blur-2xl border border-cyan-400/30 shadow-[0_0_50px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden" style={{ maxHeight: "min(70vh, 640px)" }}>
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10 bg-white/[0.02]">
        <span className="w-6 h-6 grid place-items-center rounded-lg bg-cyan-500/20 text-cyan-300"><Sparkles size={14} /></span>
        <span className="text-sm font-tactical font-semibold text-zinc-100">Command</span>
        <div className="ml-auto flex items-center gap-1">
          <button data-testid="command-dock-recents" onClick={() => setShowRecents((v) => !v)} title="Recent conversations" className="w-7 h-7 grid place-items-center rounded-lg text-zinc-400 hover:bg-white/10 hover:text-cyan-300"><History size={15} /></button>
          <button data-testid="command-dock-new" onClick={newConversation} title="New conversation" className="w-7 h-7 grid place-items-center rounded-lg text-zinc-400 hover:bg-white/10 hover:text-cyan-300"><Plus size={15} /></button>
          <button data-testid="command-dock-close" onClick={() => setOpen(false)} title="Collapse" className="w-7 h-7 grid place-items-center rounded-lg text-zinc-400 hover:bg-white/10"><ChevronUp size={15} className="rotate-180" /></button>
        </div>
      </div>

      {showRecents && (
        <div data-testid="command-dock-recent-list" className="max-h-52 overflow-y-auto thin-scroll border-b border-white/10 bg-black/20">
          {recents.length === 0 && <p className="text-xs text-zinc-500 p-3">No conversations yet.</p>}
          {recents.map((r) => (
            <button key={r.id} data-testid={`command-recent-${r.id}`} onClick={() => openSession(r.id)} className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-white/5 truncate border-b border-white/5">{r.title}</button>
          ))}
        </div>
      )}

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-3 py-3 space-y-3 min-h-[180px]">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <p className="text-sm text-zinc-300 font-tactical">Tell me what you want — I'll do it.</p>
            <p className="text-xs text-zinc-500 mt-1 mb-4">Navigate, run agents, plan multi-step jobs, or find a domain.</p>
            <div className="grid gap-1.5">
              {PROMPTS.map((p) => (
                <button key={p} data-testid={`command-prompt-${p.slice(0, 10)}`} onClick={() => send(p)} className="text-left text-xs text-cyan-200/90 bg-cyan-500/[0.06] border border-cyan-400/15 rounded-lg px-3 py-2 hover:bg-cyan-500/12 transition-colors">{p}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div key={m.id} data-testid={`command-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={`max-w-[92%] ${m.role === "user" ? "bg-cyan-500/15 border border-cyan-400/25 text-cyan-50" : "bg-white/5 border border-white/10 text-zinc-200"} rounded-xl px-3 py-2`}>
              <p className="text-[13px] whitespace-pre-wrap leading-relaxed">{m.content}</p>

              {/* single action */}
              {m.action && (
                <div data-testid={`command-action-${m.id}`} className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5">
                  <p className="text-[11px] text-zinc-400 mb-2"><span className="text-cyan-300 font-mono uppercase text-[9px] tracking-wider">{m.action.name}</span> · {m.action.summary}</p>
                  {m.action.status === "pending" && (
                    <div className="flex gap-2">
                      <button data-testid={`command-approve-${m.id}`} onClick={() => decideAction(m.id, true)} className="flex-1 flex items-center justify-center gap-1 text-xs bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-bold rounded-lg py-1.5"><Check size={13} /> Approve</button>
                      <button data-testid={`command-cancel-${m.id}`} onClick={() => decideAction(m.id, false)} className="px-3 text-xs bg-white/5 hover:bg-white/10 text-zinc-300 rounded-lg py-1.5">Cancel</button>
                    </div>
                  )}
                  {m.action.status !== "pending" && (
                    <p className={`text-[11px] font-mono ${m.action.status === "completed" ? "text-emerald-400" : m.action.status === "cancelled" ? "text-zinc-500" : "text-rose-400"}`}>{m.action.status === "completed" ? "✓ " : ""}{m.action.result || m.action.status}</p>
                  )}
                </div>
              )}

              {/* domain results (from a completed find_domain action) */}
              {m.action?.status === "completed" && m.action?.data?.domains?.results && (
                <DomainList data={m.action.data.domains} onBuy={buyDomain} />
              )}

              {/* multi-step plan */}
              {m.plan && (
                <div data-testid={`command-plan-${m.id}`} className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5">
                  <p className="text-[9px] font-mono uppercase tracking-wider text-cyan-300 mb-1.5">Plan · {m.plan.steps.length} steps · {m.plan.status}</p>
                  <ol className="space-y-1.5 mb-2">
                    {m.plan.steps.map((s) => {
                      const Icon = STEP_ICON[s.status] || CircleDot;
                      return (
                        <li key={s.idx} className="flex items-start gap-2">
                          <Icon size={14} className={`${STEP_COLOR[s.status]} mt-0.5 shrink-0 ${s.status === "executing" ? "animate-spin" : ""}`} />
                          <span className="min-w-0">
                            <span className="block text-[12px] text-zinc-200 leading-snug">{s.summary}</span>
                            {s.result && <span className="block text-[10px] text-zinc-500 mt-0.5">{s.result}</span>}
                            {s.data?.task_id && <button onClick={() => onNavigate?.("deliverables")} className="text-[10px] text-cyan-300 hover:underline mt-0.5">View deliverable →</button>}
                            {s.data?.domains?.results && <DomainList data={s.data.domains} onBuy={buyDomain} />}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                  {m.plan.status === "proposed" && (
                    <div className="flex gap-2">
                      <button data-testid={`command-plan-approve-${m.id}`} onClick={() => decidePlan(m.id, true)} className="flex-1 flex items-center justify-center gap-1 text-xs bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-bold rounded-lg py-1.5"><Check size={13} /> Approve plan</button>
                      <button data-testid={`command-plan-cancel-${m.id}`} onClick={() => decidePlan(m.id, false)} className="px-3 text-xs bg-white/5 hover:bg-white/10 text-zinc-300 rounded-lg py-1.5">Cancel</button>
                    </div>
                  )}
                  {m.plan.status === "running" && <p className="text-[10px] text-cyan-300 font-mono flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Working through the steps…</p>}
                  {m.plan.status === "paused" && <p className="text-[10px] text-amber-300 font-mono">Paused — a step needs your attention. See the note above.</p>}
                  {m.plan.status === "completed" && <p className="text-[10px] text-emerald-400 font-mono">✓ All steps completed.</p>}
                  {m.plan.status === "cancelled" && <p className="text-[10px] text-zinc-500 font-mono">Plan cancelled. Nothing was changed.</p>}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="bg-white/5 border border-white/10 rounded-xl px-3 py-2"><Loader2 size={15} className="animate-spin text-cyan-300" /></div></div>}
      </div>

      {/* input */}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-2.5 border-t border-white/10 bg-white/[0.02]">
        <input
          data-testid="command-dock-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask LifeOS to do anything…"
          className="flex-1 bg-[#05090f] border border-white/10 rounded-xl h-10 px-3 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
        />
        <button data-testid="command-dock-send" type="submit" disabled={sending || !input.trim()} className="w-10 h-10 grid place-items-center rounded-xl bg-cyan-500 hover:bg-cyan-400 text-[#04070c] disabled:opacity-40">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}

function DomainList({ data, onBuy }) {
  const results = (data.results || []).slice(0, 6);
  return (
    <div data-testid="command-domain-list" className="mt-2 space-y-1">
      {!data.registrar_connected && <p className="text-[10px] text-amber-300/90 mb-1">Connect Porkbun in Domains to enable buying.</p>}
      {results.map((r) => (
        <div key={r.domain} data-testid={`command-domain-${r.tld}`} className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/10 px-2.5 py-1.5">
          <span className="min-w-0 flex-1">
            <span className="block text-[12px] text-zinc-100 truncate">{r.domain}</span>
            <span className="block text-[10px] text-zinc-500">${r.price}{r.available === false ? " · taken" : ""}</span>
          </span>
          <button
            data-testid={`command-domain-buy-${r.tld}`}
            onClick={() => onBuy(r)}
            disabled={r.available === false}
            className="shrink-0 flex items-center gap-1 text-[11px] bg-cyan-500/90 hover:bg-cyan-400 text-[#04070c] font-bold rounded-lg px-2.5 py-1 disabled:opacity-40"
          >
            <ShoppingCart size={12} /> Buy
          </button>
        </div>
      ))}
    </div>
  );
}
